import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { GetStaticPaths, GetStaticProps, GetStaticPropsContext } from "next"
import { useRouter } from "next/router"
import Link from "next/link"
import Image from "next/image"

import * as Sentry from "@sentry/nextjs"

import { JSONAPI, OMReference, OMUniverse } from "@apptivity-lab/firmament-node-sdk"
import { fbTrackEvent, restoreServerEnvironment, useMachine, useProfile, useRestoredUniverse } from "@lib"

import { CnxBooking, CnxMeeting } from "@firmament-packages/connectx"
import { AffAttendeeProfile } from "@firmament-packages/aff"
import "@firmament-packages/connectx/extensions/CnxMeetingExtensions"

import { CnxMeetingDetailStateMachine } from "@machines/CnxMeetingDetailStateMachine"

import Breadcrumbs from "@components/Breadcrumbs"
import { CnxMeetingDetailView, UpcomingMeetingList } from "@components/domain/CnxMeeting"
import StarRatingView from "@firmament-packages/ratings/components/StarRatingView"
import LoadingIndicator from "@components/LoadingIndicator"
import ErrorHUD from "@components/ErrorHUD"
import Modal from "@components/Modal"

import AFFSidebarLayout from "@components/domain/AFFSidebarLayout"
import AffRatingList from "@firmament-packages/aff/components/AffRatingList"
import AffRatingForm from "@firmament-packages/aff/components/AffRatingForm"

type PageProps = {
    identifier: string,
    cache: {
        meeting?: JSONAPI
    }
}

const Content: React.FunctionComponent<{
    meeting: CnxMeeting,
    segmentIndex?: number,
    onBookButtonClick?: (meeting: CnxMeeting) => void,
    onCancelButtonClick?: (meeting: CnxMeeting, booking: CnxBooking) => void,
    onEnterButtonClick?: (meeting: CnxMeeting) => void,
    onRateButtonClick?: (meeting: CnxMeeting) => void
}> = ({ meeting, segmentIndex, onBookButtonClick, onCancelButtonClick, onEnterButtonClick, onRateButtonClick }) => {
    const cellsContainer = useRef<HTMLDivElement>(null)

    const attendeeProfile = OMUniverse.shared.currentUser?.user?.attendeeProfile()
    const [promptReviewForMeeting, setPromptReviewForMeeting] = useState<CnxMeeting | null>()
    useEffect(() => {
        if (promptReviewForMeeting !== undefined) {
            return
        }
        if (!attendeeProfile) {
            setPromptReviewForMeeting(null)
            return
        }

        meeting.shouldPromptLeaveRating(attendeeProfile)
            .then((meeting) => setPromptReviewForMeeting(meeting))
    }, [meeting, attendeeProfile, promptReviewForMeeting])

    const nextRating = () => {
        if (!cellsContainer.current) {
            return
        }
        cellsContainer.current.scrollTo(
            Math.min(
                (cellsContainer.current?.scrollLeft || 0) + cellsContainer.current?.clientWidth || 0,
                (cellsContainer.current?.scrollWidth || 0 - cellsContainer.current?.clientWidth || 0)
            ),
            0
        )
    }

    const previousRating = () => {
        if (!cellsContainer.current) {
            return
        }
        cellsContainer.current.scrollTo(
            Math.max(
                (cellsContainer.current?.scrollLeft || 0) - cellsContainer.current?.clientWidth || 0, 0),
                0
        )
    }

    return (
        <div className="bg-primary-200">
            <div className="p-[20px]">
                <Breadcrumbs
                    omitRoot={true}
                    labelOverrides={{
                        "classes": "Live Classes",
                        [meeting.id]: meeting.title || meeting.id
                    }} />
            </div>

            <div className="min-w-[320px] max-w-[800px] mx-auto pb-[72px]">
                <div className="bg-white-900 sm:rounded-[8px] shadow">
                    <CnxMeetingDetailView meeting={meeting} />
                </div>

                <div className="mt-[16px] py-[20px] bg-white-900 rounded-0 sm:rounded-[8px] shadow">
                    <UpcomingMeetingList meetingTemplate={meeting.meetingTemplate || undefined}
                        segmentIndex={segmentIndex}
                        meeting={meeting}
                        onBookButtonClick={onBookButtonClick}
                        onCancelButtonClick={onCancelButtonClick}
                        onEnterButtonClick={onEnterButtonClick}
                        />
                </div>

                <div id="ratingsView" className="mt-[16px] p-[20px] bg-white-900 sm:rounded-[8px] shadow">
                    <h2 className="text-title2 text-black-900">
                        {meeting.meetingTemplate ? "Class" : "AFF Expert"} Ratings
                    </h2>

                    {meeting.overallClassRatingCount() > 0 &&
                        <>
                            <div className="mt-[12px] flex sm:hidden items-center">
                                <Image src="/images/ic_star_rating_full_big.svg" width="22" height="22" alt="Class Ratings" />
                                <span className="ml-[4px] text-headline text-black-900">
                                    {meeting.overallClassRatingAverage().toFixed(1)}
                                </span>
                                <span className="text-body text-black-200 mx-[12px]">|</span>
                                <p className="text-body text-black-700">
                                    {meeting.overallClassRatingCount()} {meeting.overallClassRatingCount() === 1 ? "Review" : "Reviews"}
                                </p>
                            </div>

                            <div className="block sm:flex sm:gap-x-[32px]">
                                <div className="hidden sm:flex min-w-fit flex-col mt-[12px] gap-y-[12px]">
                                    <div>
                                        <span className="text-title1 text-black-900">
                                            {meeting.overallClassRatingAverage().toFixed(1)}
                                        </span>
                                        <span className="text-title3 text-black-700"> / 5</span>
                                    </div>

                                    <StarRatingView ratingsAverage={meeting.overallClassRatingAverage()} />

                                    <p className="text-subhead text-black-700">
                                        {meeting.overallClassRatingCount()} {meeting.overallClassRatingCount() === 1 ? "Review" : "Reviews"}
                                    </p>
                                </div>

                                <div className="mt-[12px] flex">
                                    <button type="button" className="min-w-[40px] w-[40px]"
                                        onClick={() => previousRating()}>
                                        <Image width="24" height="24"
                                            src="/images/ic_chevron_left_accent_big.svg"
                                            alt="Recent Ratings" />
                                    </button>

                                    <AffRatingList containerRef={cellsContainer} subject={meeting} />

                                    <button type="button" className="min-w-[40px] w-[40px]"
                                        onClick={() => nextRating()}>
                                        <Image width="24" height="24"
                                            src="/images/ic_chevron_right_accent_big.svg"
                                            alt="Previous Ratings" />
                                    </button>
                                </div>
                            </div>
                        </>
                    }

                    {meeting.overallClassRatingCount() === 0 &&
                        <div className="flex flex-col justify-center">
                            <Image width="262" height="196" src="/images/ic_graphic_empty_rating.svg" alt="AFF Expert Ratings" />
                            <p className="mt-[8px] text-body text-black-700 text-center">No ratings yet.</p>
                        </div>
                    }

                    <hr className="bg-black-100 my-[24px]" />

                    <div className="flex justify-between">
                        {promptReviewForMeeting &&
                            <button className="flex items-center gap-x-[12px]" onClick={() => onRateButtonClick && onRateButtonClick(meeting)}>
                                <Image width="24" height="24" src="/images/ic_leave_review_primary.svg" alt="Leave a review" />
                                <span className="link-text-primary">Leave a Review</span>
                            </button>
                        }
                    </div>
                </div>
            </div>
        </div>
    )
}

function CnxMeetingDetailPage({ identifier, cache }: PageProps) {
    const router = useRouter()
    const [machine, machineState] = useMachine(CnxMeetingDetailStateMachine, cache)

    const attendeeProfile = useProfile(AffAttendeeProfile)
    const currentSubscription = useMemo(() => attendeeProfile?.currentSubscription(), [attendeeProfile])

    const [unstartedMeeting, setUnstartedMeeting] = useState<CnxMeeting>()

    useRestoredUniverse(useMemo(() => () => {
        machine.load(new OMReference(CnxMeeting, identifier))
    }, [machine, identifier]))

    // Hold current month segment value
    const currentMonthSegmentRef = useRef<number>(0)

    const bookButtonHandler = useCallback((meeting: CnxMeeting) => {
        if (attendeeProfile) {
            fbTrackEvent("Schedule")
            machine.book(attendeeProfile, meeting)

            if (meeting.scheduledStartAt) {
                currentMonthSegmentRef.current = Math.min(meeting.scheduledStartAt.getMonth() - new Date().getMonth(), 1)
            }
        } else {
            router.push(`/auth/register?return=${router.asPath}`)
        }
    }, [machine, attendeeProfile, router])

    const cancelBookingButtonHandler = useCallback((meeting: CnxMeeting, booking: CnxBooking) => {
        if (attendeeProfile) {
            machine.cancelBooking(attendeeProfile, meeting, booking)
        }
    }, [machine, attendeeProfile])

    const addRatingButtonHandler = useCallback((meeting: CnxMeeting) => {
        if (attendeeProfile) {
            machine.showRatingForm(attendeeProfile)
        }
    }, [machine, attendeeProfile])

    const enterMeetingButtonHandler = useCallback((meeting: CnxMeeting) => {
        if (!meeting.isLiveNow()) {
            setUnstartedMeeting(meeting)
            return
        }
        if (!attendeeProfile) {
            return
        }

        meeting.join(attendeeProfile.id)
            .catch((error) => Sentry.captureException(error))

        const videoRoomUrl = meeting.videoRoom?.actualObject?.roomUrl
        if (!videoRoomUrl) {
            Sentry.captureException(`Video room is ${videoRoomUrl} attendee attempts to join meeting`)
        }

        // window.open() will be blocked by browser popup blocks if it's
        // not called directly from a user called function
        try {
            window.open(videoRoomUrl || "", "_blank")
        } catch (error) {
            Sentry.captureException(error)
        }
    }, [attendeeProfile])

    return (
        <div>
            {(() => {
                switch (machineState.name) {
                    case "presenting":
                        return <Content
                            meeting={machineState.meeting}
                            segmentIndex={currentMonthSegmentRef.current}
                            onBookButtonClick={bookButtonHandler}
                            onCancelButtonClick={cancelBookingButtonHandler}
                            onEnterButtonClick={enterMeetingButtonHandler}
                            onRateButtonClick={addRatingButtonHandler}
                            />
                    case "performingBooking":
                    case "performingBookingCancellation":
                    case "showingBookingError":
                        return (
                            <>
                                {machineState.name === "performingBooking" && <LoadingIndicator message="Processing..." />}
                                {machineState.name === "performingBookingCancellation" && <LoadingIndicator message="Cancelling..." />}
                                {machineState.name === "showingBookingError" && <ErrorHUD error={machineState.error} sticky={true} onDismiss={() => machine.back()} />}
                                <Content
                                    meeting={machineState.meeting}
                                    segmentIndex={currentMonthSegmentRef.current}
                                    onBookButtonClick={bookButtonHandler}
                                    onCancelButtonClick={cancelBookingButtonHandler}
                                    onEnterButtonClick={enterMeetingButtonHandler}
                                    onRateButtonClick={addRatingButtonHandler}
                                    />
                            </>
                        )
                    case "showingInsufficientBalance":
                        return (
                            <>
                                <Modal show={true} onDismiss={() => {
                                    machine.back()
                                }}>
                                    <div className="sm:px-[20px] sm:pb-[20px] flex flex-col align-middle  text-center">
                                        <Image width="262" height="192" objectFit="contain" src="/images/ic_graphic_not_enough_balance.svg" alt="Get More Class Passes Now" />
                                        <h4 className="mt-[20px] text-title3 text-black-900">
                                        {!currentSubscription ? "You are on the free plan and can only join free classes" : "You have used all your class passes this month"}
                                        </h4>
                                        <Link href="/profile/membership">
                                            <a className="mt-[32px] button-accent-filled w-full">Get More Class Passes Now</a>
                                        </Link>
                                    </div>
                                </Modal>
                                <Content
                                    meeting={machineState.meeting}
                                    segmentIndex={currentMonthSegmentRef.current}
                                    onBookButtonClick={bookButtonHandler}
                                    onCancelButtonClick={cancelBookingButtonHandler}
                                    onEnterButtonClick={enterMeetingButtonHandler}
                                    onRateButtonClick={addRatingButtonHandler}
                                    />
                            </>
                        )
                    case "showingMeetingBooked":
                        return (
                            <>
                                <Modal show={true} onDismiss={() => machine.load(new OMReference(CnxMeeting, identifier))}>
                                    <div className="sm:px-[20px] sm:pb-[20px] flex flex-col align-middle  text-center">
                                        <Image width="262" height="192" objectFit="contain" src="/images/ic_graphic_class_booked.svg" alt="Session Booked Successfully" />

                                        <h4 className="mt-[20px] text-title3 text-black-900">
                                            Session Booked Successfully
                                        </h4>
                                        <h5 className="mt-[24px] text-subhead text-black-700">{machineState.meeting.title}</h5>
                                        <p className="mt-[4px] text-callout text-tertiary-900">
                                            {machineState.meeting.longScheduledDateDisplay(attendeeProfile?.timezone || undefined)}
                                        </p>

                                        <p className="mt-[24px] text-subhead text-black-900 font-bold">
                                            To join this class, come back to the Live Schedule 10 minutes before the class start
                                            time.
                                        </p>

                                        <a href="https://www.loom.com/share/360f5226e6dd4debaa8e0f48e7c1eba7" target="_blank" rel="noopener noreferrer">
                                            <div className="mt-[20px] text-callout text-center uppercase underline hover:text-accent-900">
                                                Watch Tutorial
                                            </div>
                                        </a>

                                        <button type="button" className="mt-[32px] button-accent-filled w-full"
                                            onClick={() => machine.load(new OMReference(CnxMeeting, identifier))}>
                                            Done
                                        </button>

                                        <p className="mt-[32px] text-black-700">Made a mistake?</p>
                                        <button type="button" className="mt-[8px] link-text-primary"
                                            onClick={() => cancelBookingButtonHandler(machineState.meeting, machineState.booking)}>
                                            Undo Booking
                                        </button>
                                    </div>
                                </Modal>
                                <Content
                                    meeting={machineState.meeting}
                                    segmentIndex={currentMonthSegmentRef.current}
                                    onBookButtonClick={bookButtonHandler}
                                    onCancelButtonClick={cancelBookingButtonHandler}
                                    onEnterButtonClick={enterMeetingButtonHandler}
                                    onRateButtonClick={addRatingButtonHandler}
                                    />
                            </>
                        )
                    case "showingRatingForm":
                    case "savingRating":
                    case "showingRatingError":
                        return (
                            <>
                                <Modal title={<span className="text-footnote text-black-700">Leave A Review</span>} show={true} onDismiss={() => {
                                    machine.back()
                                }}>
                                    <div className="sm:px-[20px] sm:pb-[20px] flex flex-col align-middle">
                                        <AffRatingForm
                                            subject={machineState.meeting}
                                            error={machineState.name === "showingRatingError" ? machineState.error : undefined}
                                            submitting={machineState.name === "savingRating"}
                                            onSubmit={(rating) => attendeeProfile && machine.saveRating(rating, attendeeProfile)}
                                            />
                                    </div>
                                </Modal>

                                <Content
                                    meeting={machineState.meeting}
                                    segmentIndex={currentMonthSegmentRef.current}
                                    onBookButtonClick={bookButtonHandler}
                                    onCancelButtonClick={cancelBookingButtonHandler}
                                    onEnterButtonClick={enterMeetingButtonHandler}
                                    onRateButtonClick={addRatingButtonHandler}
                                    />
                            </>
                        )
                    case "showingError":
                        return (
                            <>
                                <ErrorHUD error={machineState.error} sticky={true} onDismiss={() => machine.back()} />
                                {machineState.meeting && <Content
                                    meeting={machineState.meeting}
                                    segmentIndex={currentMonthSegmentRef.current}
                                    onBookButtonClick={bookButtonHandler}
                                    onCancelButtonClick={cancelBookingButtonHandler}
                                    onEnterButtonClick={enterMeetingButtonHandler}
                                    onRateButtonClick={addRatingButtonHandler}
                                    />}
                            </>
                        )
                    default:
                        return <></>
                }
            })()}

            <Modal show={unstartedMeeting !== undefined} onDismiss={() => setUnstartedMeeting(undefined)}>
                <div className="sm:px-[20px] sm:pb-[20px] flex flex-col align-middle  text-center">
                    <Image width="262" height="192" objectFit="contain" src="/images/ic_graphic_alert.svg" alt="Class Haven't Start Yet" />

                    <h4 className="mt-[20px] text-title3 text-black-900">
                        {"This class hasn't started yet."}
                    </h4>

                    <p className="mt-[24px] text-subhead text-black-700">
                        Please come back and press <b>JOIN CLASS</b> on
                    </p>
                    <p className="mt-[4px] text-callout text-tertiary-900 text-center">
                        {unstartedMeeting?.joinAtTimeDisplay(attendeeProfile?.timezone || undefined)}
                    </p>

                    <button type="button" className="mt-[32px] button-accent-filled w-full"
                        onClick={() => setUnstartedMeeting(undefined)}>
                        Done
                    </button>
                </div>
            </Modal>
        </div>
    )
}

CnxMeetingDetailPage.Layout = AFFSidebarLayout

// This is required when we use getStaticProps() on dynamic paths.
export const getStaticPaths: GetStaticPaths<{}> = async () => {
    return {
        // Define paths which will be pre-rendered (at build time)
        paths: [],
        // Set fallback to "blocking", so that if path has never been visited,
        // the HTML will be generated and then cached for future requests
        fallback: "blocking"
    }
}

export const getStaticProps: GetStaticProps<
    PageProps,
    any,
    any
> = async (context: GetStaticPropsContext) => {
    const identifier = context.params?.identifier

    if (!identifier || typeof identifier !== "string") {
        return {
            notFound: true
        }
    }

    try {
        await restoreServerEnvironment()

        const query = CnxMeeting.query()
            .include("serviceTier")
            .include("meetingTemplate")
            .include("meetingTemplate.coverPhoto" as any)
            .include("videoRoom")
            .include("bookings")
            .include("coverPhoto")
            .include("categories")
            .include("provider")
            .include("provider.photo" as any)
            .filterGroup([
                ["id", "equals", identifier],
                ["slug", "equals", identifier]
            ], "OR")

        await query.execute()

        if (query.meta.count === 0) {
            // We are using getStaticProps() to pre-qualify an identity identifier
            // so if a matching one isn't found, throw an error.
            throw new Error(`Meeting with identifier "${identifier}" not found.`)
        }

        return {
            props: {
                identifier,
                cache: {
                    meeting: query.response.data
                }
            },
            // Next.js will attempt to re-generate the page:
            // - When a request comes in
            // - At most once every 900 seconds
            revalidate: 900
        }
    } catch (error: unknown) {
        return {
            notFound: true
        }
    }
}

export default CnxMeetingDetailPage
